<template>
  <div>
    <a-modal title="待办工作台" :visible="visible" :closable="false" :keyboard="canClose"
             wrapClassName="system-todo-workspace" :maskClosable="false" :footer="null"
             width="100%" :bodyStyle="{padding: 0}" @cancel="close">
      <div class="todo-workspace">
        <header class="todo-header">
          <div class="todo-brand"><span class="todo-brand-icon"><a-icon type="inbox"/></span>
            <div><h1>待办工作台</h1><p>集中查看提醒，及时处理每一项事务</p></div>
          </div>
          <div class="todo-actions">
            <span class="todo-sync" role="status">{{ loading ? '正在更新…' : error ? '更新失败' : '自动更新中' }}</span>
            <a-button :loading="loading" icon="reload" @click="refresh">刷新</a-button>
            <a-button v-if="admin" icon="setting" @click="adminVisible = true">管理待办</a-button>
            <a-button v-if="canClose" @click="close">返回业务</a-button>
            <a-button type="link" @click="logout">退出登录</a-button>
          </div>
        </header>
        <div class="todo-layout">
          <aside class="todo-sidebar" aria-label="事项分类">
            <div class="todo-nav-label">我的工作</div>
            <div class="todo-nav-active"><a-icon type="appstore"/><span>全部待办</span><b>{{ countText(total) }}</b></div>
            <div class="todo-nav-label todo-nav-section">事项类型</div>
            <div class="todo-category"><a-icon type="file-text"/><span>单据处理</span><small>已接入</small></div>
            <div v-for="item in futureCategories" :key="item.name" :class="['todo-category', {'todo-category-future': !item.ready}]">
              <a-icon :type="item.icon"/><span>{{ item.name }}</span><small>{{ item.ready ? '已接入' : '待接入' }}</small>
            </div>
            <div class="todo-sidebar-note"><a-icon type="safety-certificate"/><p>统一提醒 · 按权处理</p><span>各类事项使用对应的业务处理流程。</span></div>
          </aside>
          <main class="todo-main">
            <a-alert v-if="error" type="error" show-icon :message="error" description="暂时无法确认最新待办，请刷新重试。"/>
            <div class="todo-overview">
              <div class="todo-intro"><span class="todo-eyebrow">我的待办</span><h2>把需要关注的事，处理好</h2><p>强制事项优先展示，其余事项按应处理时间排列。</p></div>
              <div class="todo-counts">
                <div><span>待处理</span><strong>{{ countText(total) }}</strong></div>
                <div class="todo-count-critical"><span>强制处理</span><strong>{{ countText(mandatoryCount) }}</strong></div>
              </div>
            </div>
            <div v-if="mandatoryCount > 0 && !error" class="todo-notice" role="status">
              <a-icon type="exclamation-circle"/><div><b>还有 {{ mandatoryCount }} 项强制待办需要处理</b><span>完成后可继续其他业务。暂时无法处理时，请联系管理员延期。</span></div>
            </div>
            <div class="todo-content">
              <section class="todo-list-panel" aria-label="待办列表">
                <div class="todo-panel-title"><h3>待处理事项 <span>{{ countText(total) }}</span></h3><span>点击事项或「去处理」打开</span></div>
                <a-table rowKey="id" size="small" :columns="columns" :dataSource="rows" :loading="loading"
                         :pagination="pagination" :scroll="{x: 880}" @change="changePage">
                  <template slot="emptyText">
                    <div class="todo-empty"><a-icon :type="error ? 'disconnect' : 'check-circle'"/>
                      <h3>{{ error ? '待办暂时无法加载' : initializing ? '正在获取待办' : '当前没有待处理事项' }}</h3>
                      <p>{{ error ? '请点击顶部刷新按钮重试' : '新的待办将在这里集中展示' }}</p>
                    </div>
                  </template>
                  <template slot="sourceId" slot-scope="text, record">
                    <div class="todo-subject"><span class="todo-item-icon"><a-icon type="file-text"/></span><div>
                      <a v-if="canOpenReceipt(record)" class="todo-subject-link" @click="openBill(record)">{{ record.title || text }}</a>
                      <span v-else class="todo-subject-link">{{ record.title || text || '待办事项' }}</span>
                      <span class="todo-secondary">{{ receiptLabel(record) }}</span>
                      <span v-if="record.content" class="todo-secondary">{{ record.content }}</span>
                    </div></div>
                  </template>
                  <template slot="organizationName" slot-scope="text, record"><div class="todo-object">{{ text || '—' }}<span v-if="record.sourceType === 'SALE_INPUT'" class="todo-secondary">发货方：{{ record.sendOrganizationName || '—' }}</span></div></template>
                  <template slot="dueTime" slot-scope="text, record"><span class="todo-time">{{ text || '—' }}</span><span class="todo-secondary">创建于 {{ record.billCreateTime || '—' }}</span></template>
                  <template slot="forceFlag" slot-scope="text, record"><span :class="['todo-priority', {'is-mandatory': text}]">{{ text ? '强制处理' : '普通提醒' }}</span><span class="todo-secondary">{{ overdueLabel(record.overdueHours) }}</span></template>
                  <template slot="action" slot-scope="text, record"><a-button v-if="canOpenReceipt(record)" type="primary" size="small" :disabled="openingBill" @click="openBill(record)">去处理 <a-icon type="arrow-right"/></a-button><span v-else class="todo-secondary">暂不支持处理</span></template>
                </a-table>
              </section>
              <aside class="todo-guide">
                <h3><a-icon type="compass"/> 处理指引</h3>
                <ol><li><b>查看事项</b><p>确认关联组织、截止时间和提醒方式。</p></li><li><b>进入业务处理</b><p>点击「去处理」，在原业务窗口中完成操作。</p></li><li><b>确认处理结果</b><p>关闭业务窗口后自动更新待办状态。</p></li></ol>
                <div class="todo-help"><a-icon type="question-circle"/><b>暂时无法处理？</b><p>请联系管理员说明原因，申请延期。延期后，到期仍未完成的事项会再次提醒。</p></div>
              </aside>
            </div>
            <footer class="todo-footnote"><a-icon type="sync"/> 待办状态自动更新 · 操作权限以原业务为准</footer>
          </main>
        </div>
      </div>
    </a-modal>
    <component :is="billComponent" v-if="billComponent" ref="bill" @ok="refresh" @show="billShown"/>
    <a-modal v-if="adminVisible" title="系统待办管理" :visible="true" :width="'96%'" :footer="null"
             :maskClosable="false" @cancel="adminVisible = false; refresh()">
      <system-todo-list embedded @changed="refresh"/>
    </a-modal>
  </div>
</template>

<script>
import {getAction} from '@/api/manage'
import {todoEvents, todoState} from '@/utils/systemTodo'
import {checkPermission} from '@/utils/hasPermission'

export default {
  name: 'SystemTodoModal',
  components: {
    SystemTodoList: () => import('@/views/setting/system/SystemTodoList')
  },
  props: {pollInterval: {type: Number, default: 30000}},
  data() {
    return {
      // 首次查询完成前不展示弹框，避免空待办在请求返回前短暂闪现。
      visible: false,
      initializing: true,
      loading: false,
      error: '',
      admin: false,
      mandatoryCount: 0,
      rows: [],
      pageNo: 1,
      total: 0,
      manual: false,
      billComponent: null,
      openingBill: false,
      billVisible: false,
      adminVisible: false,
      timer: null,
      disposed: false,
      refreshAgain: false,
      futureCategories: [
        {name: '库存预警', icon: 'alert'},
        {name: '接口对接', icon: 'api'},
        {name: '数据异常', icon: 'database', ready: true}
      ],
      columns: [
        {title: '事项', dataIndex: 'sourceId', width: 225, scopedSlots: {customRender: 'sourceId'}},
        {title: '关联组织', dataIndex: 'organizationName', width: 180, scopedSlots: {customRender: 'organizationName'}},
        {title: '应处理时间', dataIndex: 'dueTime', width: 205, scopedSlots: {customRender: 'dueTime'}},
        {title: '提醒方式', dataIndex: 'forceFlag', width: 130, scopedSlots: {customRender: 'forceFlag'}},
        {title: '操作', key: 'action', width: 110, fixed: 'right', scopedSlots: {customRender: 'action'}}
      ]
    }
  },
  computed: {
    canClose() {
      return !this.initializing && !this.error && this.mandatoryCount === 0
    },
    pagination() {
      return {current: this.pageNo, pageSize: 20, total: this.total, showTotal: total => '共 ' + total + ' 笔'}
    }
  },
  created() {
    todoEvents.$on('required', this.required)
    todoEvents.$on('open', this.open)
    this.refresh()
  },
  beforeDestroy() {
    this.disposed = true
    clearTimeout(this.timer)
    todoEvents.$off('required', this.required)
    todoEvents.$off('open', this.open)
    Object.assign(todoState, {enabled: false, admin: false, mandatoryCount: 0})
  },
  methods: {
    countText(value) {
      return this.initializing || this.error ? '—' : value
    },
    // 后续来源接入独立处理入口，未知类型不能误打开收货单。
    canOpenReceipt(record) {
      return record.sourceType === 'SALE_INPUT' && ['2', '3'].includes(record.billType)
    },
    receiptLabel(record) {
      if (record.reminderType === 'BI_INVENTORY_CHANGE_MISMATCH') return '数据异常 · BI 库存变动对比'
      if (!this.canOpenReceipt(record)) return '其他事项'
      return record.billType === '2' ? '直营收货单' : '加盟收货单'
    },
    overdueLabel(hours) {
      if (hours == null) return '—'
      return Number(hours) < 1 ? '超时不足 1 小时' : '已超时 ' + hours + ' 小时'
    },
    async refresh() {
      if (this.disposed) return
      if (this.loading) {
        this.refreshAgain = true;
        return
      }
      clearTimeout(this.timer)
      this.loading = true
      try {
        const res = await getAction('/system/todo/mine', {pageNo: this.pageNo, pageSize: 20})
        if (this.disposed) return
        if (!res.success) throw new Error(res.message || '待办检查失败，请重试')
        const data = res.result
        const firstCheck = this.initializing
        this.rows = data.page.records || []
        this.total = data.page.total || 0
        this.mandatoryCount = data.mandatoryCount || 0
        if (this.mandatoryCount > 0) this.manual = false
        else if (firstCheck && this.rows.length > 0) this.manual = true
        this.admin = !!data.admin
        this.error = ''
        this.initializing = false
        Object.assign(todoState, {enabled: !!data.enabled, admin: this.admin, mandatoryCount: this.mandatoryCount})
        // 子单据还在处理时不卸载它，避免丢失未保存数据；返回列表后再消失。
        this.visible = this.mandatoryCount > 0 || this.billVisible || this.adminVisible || this.manual
        if (!this.rows.length && this.pageNo > 1) {
          this.pageNo = 1;
          this.refreshAgain = true
        }
      } catch (e) {
        if (!this.disposed) {
          this.error = e.message || '无法检查待办，请刷新或退出后重试'
          // 查询失败时不展示空白待办框，等待后续轮询或用户刷新成功后再显示。
          this.visible = false
        }
      } finally {
        if (!this.disposed) {
          this.loading = false
          const wait = this.refreshAgain ? 0 : Math.max(this.pollInterval, 5000)
          this.refreshAgain = false
          this.timer = setTimeout(() => this.refresh(), wait)
        }
      }
    },
    required() {
      this.visible = true;
      this.refresh()
    },
    open() {
      this.manual = true;
      this.visible = true;
      this.refresh()
    },
    close() {
      if (this.canClose) {
        this.manual = false;
        this.visible = false
      }
    },
    changePage(page) {
      this.pageNo = page.current;
      this.refresh()
    },
    async openBill(record) {
      if (this.openingBill || !this.canOpenReceipt(record)) return
      const moduleKey = record.billType === '2' ? 'DeptSaleInput' : 'JoinSaleInput'
      if (!checkPermission(moduleKey + ':audit')) {
        this.$message.warning('当前账号可能没有审核权限，请联系管理员核对收货单权限；仍可浏览单据')
      }
      this.openingBill = true
      try {
        const [module, response] = await Promise.all([
          record.billType === '2'
              ? import('@/views/stock/in/modal/DeptSaleInputModal')
              : import('@/views/stock/in/modal/JoinSaleInputModal'),
          getAction('/bill/saleInput/view', {id: record.sourceId})
        ])
        if (this.disposed) return
        if (!response.success || !response.result || !response.result.id) {
          throw new Error(response.message || '单据不存在或状态已变化，请刷新')
        }
        this.billComponent = module.default
        await this.$nextTick()
        if (!this.disposed) {
          // 同现有列表一样调用 handleView，但先加载完整单据，不能编辑待办投影。
          this.visible = true
          this.$refs.bill.disableSubmit = true
          this.$refs.bill.title = '浏览'
          this.$refs.bill.handleView(response.result)
        }
      } catch (e) {
        if (!this.disposed) {
          this.$message.error(e.message || '单据弹框加载失败，请重试')
          this.refresh()
        }
      } finally {
        if (!this.disposed) this.openingBill = false
      }
    },
    billShown(value) {
      this.billVisible = value
      if (!value) this.refresh()
    },
    async logout() {
      await this.$store.dispatch('Logout')
      this.$router.push('/user/login')
    }
  }
}
</script>

<style>
/* Modal 挂载到 body，覆盖仅限定在本工作台，子单据保持原有弹窗层级。 */
.system-todo-workspace .ant-modal { top: 0; margin: 0; padding: 0; max-width: none; }
.system-todo-workspace .ant-modal-content { border-radius: 0; box-shadow: none; }
.system-todo-workspace .ant-modal-header { position: absolute; width: 1px; height: 1px; overflow: hidden; padding: 0; border: 0; clip: rect(0, 0, 0, 0); }
.system-todo-workspace .ant-modal-body { padding: 0; }
</style>

<style scoped>
.todo-workspace { height: 100vh; height: 100dvh; display: flex; flex-direction: column; background: #f4f6f9; color: #263449; }
.todo-header { min-height: 80px; padding: 16px 24px; background: #fff; border-bottom: 1px solid #e8ecf2; display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-shrink: 0; }
.todo-brand { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
.todo-brand-icon { width: 40px; height: 40px; background: #edf4ff; color: #3478d4; border-radius: 10px; display: grid; place-items: center; font-size: 22px; }
.todo-brand h1 { font-size: 18px; font-weight: 600; margin: 0 0 3px; color: #203047; }
.todo-brand p { margin: 0; color: #8290a3; font-size: 12px; }
.todo-actions { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
.todo-sync { color: #8290a3; font-size: 12px; margin-right: 12px; }
.todo-layout { display: flex; flex: 1; min-height: 0; }
.todo-sidebar { width: 200px; flex-shrink: 0; background: #fff; border-right: 1px solid #e8ecf2; padding: 24px 12px; display: flex; flex-direction: column; overflow-y: auto; }
.todo-nav-label { font-size: 11px; color: #8c98a9; margin: 0 12px 10px; letter-spacing: 1px; }
.todo-nav-section { margin-top: 32px; }
.todo-nav-active, .todo-category { display: flex; align-items: center; gap: 10px; padding: 12px; border-radius: 6px; font-size: 13px; }
.todo-nav-active { color: #2c70c7; background: #edf4ff; font-weight: 500; }
.todo-nav-active b { margin-left: auto; font-size: 12px; }
.todo-category small { margin-left: auto; font-size: 10px; color: #8995a6; }
.todo-category-future { color: #939dad; }
.todo-sidebar-note { margin-top: auto; padding: 40px 12px 4px; color: #909cad; font-size: 12px; }
.todo-sidebar-note > .anticon { color: #688bb8; font-size: 20px; }
.todo-sidebar-note p { margin: 10px 0 6px; color: #68778c; }
.todo-main { flex: 1; min-width: 0; overflow-y: auto; padding: 24px; }
.todo-overview { display: flex; justify-content: space-between; align-items: center; gap: 24px; margin-bottom: 24px; }
.todo-eyebrow { color: #68829f; font-size: 12px; }
.todo-intro h2 { margin: 8px 0; color: #203047; font-size: 22px; font-weight: 600; }
.todo-intro p { color: #8190a2; font-size: 12px; margin: 0; }
.todo-counts { display: flex; gap: 36px; padding-right: 24px; flex-shrink: 0; }
.todo-counts > div { display: flex; flex-direction: column; min-width: 65px; }
.todo-counts span { color: #76859a; font-size: 12px; }
.todo-counts strong { font-size: 30px; line-height: 1.5; font-weight: 600; font-variant-numeric: tabular-nums; }
.todo-count-critical strong { color: #c87a24; }
.todo-notice { display: flex; gap: 12px; align-items: flex-start; background: #fff8ed; border: 1px solid #f4e4c9; border-radius: 6px; padding: 12px 16px; margin-bottom: 16px; }
.todo-notice > .anticon { color: #c58a38; margin-top: 3px; }
.todo-notice b { color: #906225; font-size: 13px; font-weight: 500; }
.todo-notice span { display: block; font-size: 12px; color: #a1845b; margin-top: 4px; }
.todo-content { display: flex; align-items: flex-start; gap: 16px; }
.todo-list-panel { flex: 1; min-width: 0; background: #fff; border: 1px solid #e8ecf2; border-radius: 8px; overflow: hidden; }
.todo-panel-title { min-height: 60px; padding: 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; border-bottom: 1px solid #edf0f4; }
.todo-panel-title h3, .todo-guide h3 { margin: 0; font-size: 14px; font-weight: 600; color: #34445b; }
.todo-panel-title h3 span { margin-left: 8px; background: #f1f4f8; padding: 2px 7px; border-radius: 4px; font-size: 12px; color: #8190a2; }
.todo-panel-title > span { font-size: 12px; color: #8b97a7; }
.todo-subject { display: flex; align-items: center; gap: 10px; padding: 10px 0; }
.todo-item-icon { background: #f0f5fc; color: #7193c1; padding: 9px; border-radius: 6px; }
.todo-subject-link { font-weight: 500; font-size: 13px; }
.todo-secondary { display: block; color: #8995a6; font-size: 11px; margin-top: 6px; overflow-wrap: anywhere; }
.todo-object { font-size: 13px; overflow-wrap: anywhere; }
.todo-time { font-size: 12px; font-variant-numeric: tabular-nums; }
.todo-priority { display: inline-block; padding: 3px 8px; background: #edf4ff; color: #557ca9; border-radius: 4px; font-size: 11px; }
.todo-priority.is-mandatory { background: #fff3e3; color: #b87929; }
.todo-guide { width: 220px; flex-shrink: 0; background: #fff; border: 1px solid #e8ecf2; border-radius: 8px; padding: 20px; }
.todo-guide h3 .anticon { color: #7193c1; margin-right: 5px; }
.todo-guide ol { padding: 0; margin: 24px 0; list-style: none; counter-reset: steps; }
.todo-guide li { position: relative; padding-left: 29px; margin-bottom: 20px; counter-increment: steps; font-size: 12px; }
.todo-guide li::before { content: counter(steps); position: absolute; left: 0; top: 0; width: 20px; height: 20px; border-radius: 50%; background: #f0f4fa; color: #7b92ad; text-align: center; line-height: 20px; font-size: 11px; }
.todo-guide b { font-weight: 500; color: #526279; }
.todo-guide p { color: #8895a7; font-size: 12px; line-height: 1.8; margin: 6px 0 0; }
.todo-help { border-top: 1px solid #edf0f4; padding-top: 18px; font-size: 12px; }
.todo-help > .anticon { color: #8598b0; margin-right: 6px; }
.todo-empty { padding: 70px 12px; }
.todo-empty > .anticon { font-size: 36px; color: #9bb9aa; }
.todo-empty h3 { font-size: 14px; margin: 16px 0 8px; color: #56687d; }
.todo-empty p { color: #8b97a7; font-size: 12px; }
.todo-footnote { padding: 18px 0; font-size: 11px; color: #909cad; }
.todo-main > .ant-alert { margin-bottom: 16px; }
@media (max-width: 1400px) { .todo-guide { display: none; } }
@media (max-width: 1000px) { .todo-sidebar { width: 165px; padding: 20px 8px; } .todo-main { padding: 16px; } .todo-sync { display: none; } .todo-counts { gap: 20px; padding-right: 0; } .todo-intro h2 { font-size: 18px; } }
@media (max-width: 760px) { .todo-header { padding: 12px 16px; flex-wrap: wrap; gap: 12px; } .todo-actions { width: 100%; overflow-x: auto; } .todo-sidebar { display: none; } .todo-overview { align-items: flex-start; flex-wrap: wrap; gap: 16px; } .todo-counts { gap: 40px; } .todo-counts strong { font-size: 24px; } .todo-panel-title > span { display: none; } }
</style>
